import { createSlice, PayloadAction } from '@reduxjs/toolkit';
export type User = {
    Id: string | undefined;
    UserIdentifier: string | undefined;
    PhoneNumber: string | undefined | null;
    Language: string | undefined;
    FirstName: string | undefined;
    LastName: string | undefined;
    Email: string | undefined | null;
};

export type TUserInfo = {
    token?: string | null;
    roles?: string[] | null;
    /** Organization-scoped role: owner | technician */
    role?: string | null;
    organizationId?: number | null;
    organizationName?: string | null;
    userId?: number | undefined;
    username?: string | undefined;
    email?: string | undefined;
    passwordHash?: string | undefined;
    createdAt?: string | undefined;
} & Partial<User>;

/** Profile/org metadata only — never persist the access token (XSS). */
const withoutToken = (info: TUserInfo): Omit<TUserInfo, 'token'> => {
    const safe = { ...info };
    delete safe.token;
    return safe;
};

/**
 * Persist profile/org metadata only. Never write the access token —
 * Keycloak keeps it in memory (`keycloak.token`); localStorage would
 * expose it to XSS.
 */
const persistUserInfo = (state: TUserInfo) => {
    const localState = JSON.parse(
        window.localStorage.getItem('userInfo') || '{}'
    ) as TUserInfo;
    window.localStorage.setItem(
        'userInfo',
        JSON.stringify({
            ...withoutToken(localState),
            ...withoutToken(state),
        })
    );
};

const loadPersistedUserInfo = (): TUserInfo => {
    try {
        const raw = window.localStorage.getItem('userInfo');
        if (!raw) return {};
        const parsed = JSON.parse(raw) as TUserInfo;
        if (parsed.token) {
            // One-time migration: drop any JWT that was persisted before
            // this fix so a refresh cannot re-expose it.
            const safe = withoutToken(parsed);
            window.localStorage.setItem('userInfo', JSON.stringify(safe));
            return safe;
        }
        return parsed;
    } catch {
        return {};
    }
};

const initialState: TUserInfo = loadPersistedUserInfo();

const userInfoSlice = createSlice({
    name: 'userInfo',
    initialState,
    reducers: {
        setUserInfo: (state: TUserInfo, action: PayloadAction<TUserInfo>) => {
            Object.entries(action.payload).forEach(([key, value]) => {
                // A little bit of magic to make sure that the state is immutable
                state[key as keyof TUserInfo] = value as any;
            });
            persistUserInfo(state);
        },
        removeUserInfo: () => {
            window.localStorage.removeItem('userInfo');
            return {};
        },
        /**
         * In-memory only. Callers that need a bearer should prefer
         * `getValidToken()` / `keycloak.token` — this exists for session
         * UI that still reads `userInfo.token` from Redux.
         */
        setToken: (state: TUserInfo, action: PayloadAction<string>) => {
            state.token = action.payload;
        },
    },
});

export const { setUserInfo, removeUserInfo, setToken } = userInfoSlice.actions;

export default userInfoSlice.reducer;
